/**
 * NexusReferralIntake - Referral submission form
 * Client search/create, demographics, clinical need, payer, preferences
 */
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, UserPlus, Search, Loader2 } from "lucide-react";

const SERVICE_TYPES = [
  { value: "BA", label: "Behavioral Assistance (BA)" },
  { value: "IIC", label: "Individual In-Community (IIC)" },
  { value: "BPS", label: "BPS Assessment" },
  { value: "FCT", label: "Family-Centered Treatment (FCT)" },
  { value: "PHP", label: "Partial Hospital Program (PHP)" },
  { value: "IOP", label: "Intensive Outpatient (IOP)" },
  { value: "OTHER", label: "Other" },
];

const SOURCE_TYPES = [
  { value: "internal", label: "Internal" },
  { value: "school", label: "School" },
  { value: "hospital", label: "Hospital" },
  { value: "family", label: "Family" },
  { value: "provider", label: "Provider" },
  { value: "court", label: "Court" },
  { value: "self", label: "Self" },
  { value: "other", label: "Other" },
];

const NJ_COUNTIES = [
  "Atlantic","Bergen","Burlington","Camden","Cape May","Cumberland",
  "Essex","Gloucester","Hudson","Hunterdon","Mercer","Middlesex",
  "Monmouth","Morris","Ocean","Passaic","Salem","Somerset","Sussex",
  "Union","Warren"
];

const initialForm = {
  client_first_name: "", client_last_name: "", client_dob: "",
  client_gender: "", client_county: "", client_zip: "",
  client_phone: "", client_email: "", client_medicaid_id: "",
  client_guardian_name: "", client_guardian_phone: "",
  presenting_need: "", service_requested: "", diagnosis_codes: "",
  urgency: "routine", risk_level: "low",
  payer_type: "medicaid", payer_name: "PerformCare NJ",
  source_type: "internal", source_name: "", source_contact: "",
  preferred_modality: "any", language_preference: "English",
  scheduling_constraints: "", school_district: "",
};

const NexusReferralIntake = ({ token, API, onComplete }) => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linkedClientId, setLinkedClientId] = useState(null);

  const set = (field) => (e) => {
    const val = e?.target ? e.target.value : e;
    setForm(prev => ({ ...prev, [field]: val }));
  };

  const searchClients = async () => {
    if (!clientSearch.trim()) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/clients?search=${encodeURIComponent(clientSearch)}&limit=5`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSearchResults(data.clients || data.data || []);
    } catch (err) {
      toast.error("Client search failed");
    } finally {
      setSearching(false);
    }
  };

  const linkClient = (client) => {
    setLinkedClientId(client.id);
    setForm(prev => ({
      ...prev,
      client_first_name: client.first_name || "",
      client_last_name: client.last_name || "",
      client_dob: client.date_of_birth || "",
      client_gender: client.gender || "",
      client_county: client.county || "",
      client_zip: client.zip_code || "",
      client_phone: client.phone || "",
      client_email: client.email || "",
      client_medicaid_id: client.medicaid_number || "",
      client_guardian_name: client.guardian_name || "",
      client_guardian_phone: client.guardian_phone || "",
    }));
    setSearchResults([]);
    setClientSearch("");
    toast.success(`Linked to ${client.first_name} ${client.last_name}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_first_name || !form.client_last_name) {
      toast.error("Client name is required");
      return;
    }
    if (!form.presenting_need) {
      toast.error("Presenting need is required");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        diagnosis_codes: form.diagnosis_codes ? form.diagnosis_codes.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      if (linkedClientId) {
        payload.client_id = linkedClientId;
      }
      const res = await fetch(`${API}/api/nexus/referrals`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Referral submitted successfully");
        setForm(initialForm);
        setLinkedClientId(null);
        onComplete?.();
      } else {
        toast.error(data.detail || "Failed to submit referral");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="nexus-referral-intake">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Client Search / Link */}
        <Card className="lg:col-span-2 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4 text-indigo-600" />
              Link Existing Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Input
                  data-testid="client-search-input"
                  placeholder="Search by name or Medicaid ID..."
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), searchClients())}
                />
              </div>
              <Button type="button" variant="outline" size="sm" onClick={searchClients} disabled={searching}>
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>
            {linkedClientId && (
              <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-md inline-flex items-center gap-1">
                Linked to existing client
                <button type="button" className="ml-2 text-emerald-900 underline" onClick={() => { setLinkedClientId(null); toast.info("Client unlinked"); }}>Unlink</button>
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="mt-2 border rounded-md divide-y max-h-40 overflow-y-auto">
                {searchResults.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex justify-between items-center"
                    onClick={() => linkClient(c)}
                  >
                    <span className="font-medium">{c.first_name} {c.last_name}</span>
                    <span className="text-xs text-slate-500">{c.medicaid_number || c.date_of_birth || ""}</span>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Client Demographics */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-indigo-600" />
              Client Demographics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">First Name *</Label>
                <Input data-testid="intake-first-name" value={form.client_first_name} onChange={set("client_first_name")} required />
              </div>
              <div>
                <Label className="text-xs">Last Name *</Label>
                <Input data-testid="intake-last-name" value={form.client_last_name} onChange={set("client_last_name")} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date of Birth</Label>
                <Input type="date" data-testid="intake-dob" value={form.client_dob} onChange={set("client_dob")} />
              </div>
              <div>
                <Label className="text-xs">Gender</Label>
                <Select value={form.client_gender} onValueChange={set("client_gender")}>
                  <SelectTrigger data-testid="intake-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Non-binary">Non-binary</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">County</Label>
                <Select value={form.client_county} onValueChange={set("client_county")}>
                  <SelectTrigger data-testid="intake-county"><SelectValue placeholder="Select county" /></SelectTrigger>
                  <SelectContent>
                    {NJ_COUNTIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">ZIP Code</Label>
                <Input data-testid="intake-zip" value={form.client_zip} onChange={set("client_zip")} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Phone</Label>
                <Input data-testid="intake-phone" value={form.client_phone} onChange={set("client_phone")} />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" data-testid="intake-email" value={form.client_email} onChange={set("client_email")} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Medicaid ID</Label>
              <Input data-testid="intake-medicaid" value={form.client_medicaid_id} onChange={set("client_medicaid_id")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Guardian Name</Label>
                <Input value={form.client_guardian_name} onChange={set("client_guardian_name")} />
              </div>
              <div>
                <Label className="text-xs">Guardian Phone</Label>
                <Input value={form.client_guardian_phone} onChange={set("client_guardian_phone")} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Clinical Need + Payer */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Clinical & Payer Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Presenting Need *</Label>
              <Textarea
                data-testid="intake-presenting-need"
                value={form.presenting_need}
                onChange={set("presenting_need")}
                placeholder="Describe the clinical reason for referral..."
                rows={3}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Service Requested</Label>
                <Select value={form.service_requested} onValueChange={set("service_requested")}>
                  <SelectTrigger data-testid="intake-service"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {SERVICE_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Diagnosis Codes</Label>
                <Input
                  data-testid="intake-diagnosis"
                  value={form.diagnosis_codes}
                  onChange={set("diagnosis_codes")}
                  placeholder="F43.10, F41.1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Urgency</Label>
                <Select value={form.urgency} onValueChange={set("urgency")}>
                  <SelectTrigger data-testid="intake-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="routine">Routine (72h SLA)</SelectItem>
                    <SelectItem value="urgent">Urgent (24h SLA)</SelectItem>
                    <SelectItem value="emergent">Emergent (4h SLA)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Risk Level</Label>
                <Select value={form.risk_level} onValueChange={set("risk_level")}>
                  <SelectTrigger data-testid="intake-risk"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <hr className="my-2" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Payer Type</Label>
                <Select value={form.payer_type} onValueChange={set("payer_type")}>
                  <SelectTrigger data-testid="intake-payer-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="medicaid">Medicaid</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                    <SelectItem value="self_pay">Self Pay</SelectItem>
                    <SelectItem value="uninsured">Uninsured</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Payer Name</Label>
                <Input data-testid="intake-payer-name" value={form.payer_name} onChange={set("payer_name")} />
              </div>
            </div>

            <hr className="my-2" />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Referral Source</Label>
                <Select value={form.source_type} onValueChange={set("source_type")}>
                  <SelectTrigger data-testid="intake-source-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SOURCE_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Source Name</Label>
                <Input value={form.source_name} onChange={set("source_name")} placeholder="e.g. Camden School District" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Preferred Modality</Label>
                <Select value={form.preferred_modality} onValueChange={set("preferred_modality")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="in_person">In-Person</SelectItem>
                    <SelectItem value="telehealth">Telehealth</SelectItem>
                    <SelectItem value="home_based">Home-Based</SelectItem>
                    <SelectItem value="school_based">School-Based</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Language</Label>
                <Input value={form.language_preference} onChange={set("language_preference")} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Scheduling Constraints</Label>
              <Input value={form.scheduling_constraints} onChange={set("scheduling_constraints")} placeholder="e.g. After school only" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Submit */}
      <div className="mt-4 flex justify-end">
        <Button
          type="submit"
          data-testid="submit-referral-btn"
          disabled={submitting}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-6"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
          Submit Referral
        </Button>
      </div>
    </form>
  );
};

export default NexusReferralIntake;
